import React from "react";
import { Box } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAuth } from "../../../../providers/AuthProvider";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import PrimaryButton from "../../../../common/ui/PrimaryButton";
import { RootStatePlatform } from "../../../platform/store/platform-slice";

/**
 * Dashboard CTA opening the consultation experience in a new tab.
 * Visible on both the affiliate (`/client/dashboard`) and customer
 * (`/platform/dashboard`) dashboards. The customer id is read from the URL
 * `?id=` (affiliate flow) and falls back to the current authenticated user
 * (customer flow).
 *
 * Visual reference: Figma node 14571:3383
 * https://www.figma.com/design/3ggABR5gEZnIvlHaXdO4YM/?node-id=14571-3383
 *
 * Color mapping (hex → theme):
 *   #FFFFFF → NEUTRAL_BG_MAIN
 *   #3B3F42 → NEUTRAL_TEXT_DEFAULT (lightTheme_Neutral[90])
 *   #6D7075 → NEUTRAL_TEXT_WEAK   (lightTheme_Neutral[80])
 *   #E1E3E5 → NEUTRAL_BORDER_WEAK (lightTheme_Neutral[50])
 *   #E6F0FF → colorTokens.P[10]
 *   #1064DB → PRIMARY_TEXT_MAIN   (lightTheme_Primary[80])
 */
const PersonalTrainerIcon: React.FC = () => (
  // 12×12 SVG combining the two layers from the Figma "personal-trainer"
  // composite. Uses `currentColor` so PrimaryButton's text color flows in.
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* head — original 3.75×3.75 path translated to top-center */}
    <g transform="translate(4.125, 0)">
      <path d="M1.875 3.75C2.91053 3.75 3.75 2.91053 3.75 1.875C3.75 0.839466 2.91053 0 1.875 0C0.839466 0 0 0.839466 0 1.875C0 2.91053 0.839466 3.75 1.875 3.75Z" />
    </g>
    {/* body / arms — original 10.4998×7.5 path translated to bottom */}
    <g transform="translate(0.75, 4.5)">
      <path d="M10.2381 2.43075L7.61313 0.18075C7.47719 0.0641321 7.30399 1.62326e-05 7.12488 0H3.37488C3.19577 1.62326e-05 3.02258 0.0641321 2.88663 0.18075L0.261632 2.43075C0.183231 2.49802 0.119571 2.58076 0.0746434 2.67378C0.0297159 2.7668 0.00448987 2.8681 0.000546628 2.97133C-0.00339661 3.07456 0.014028 3.17749 0.0517281 3.27367C0.0894282 3.36984 0.146591 3.4572 0.219632 3.53025L2.24988 5.5605V3.4395L1.85313 3.04275L2.99988 2.0595V6.75C2.99988 6.94891 3.0789 7.13968 3.21955 7.28033C3.3602 7.42098 3.55097 7.5 3.74988 7.5H6.74988C6.9488 7.5 7.13956 7.42098 7.28021 7.28033C7.42086 7.13968 7.49988 6.94891 7.49988 6.75V2.0595L8.64663 3.04275L8.24988 3.4395V5.5605L10.2801 3.53025C10.3532 3.4572 10.4103 3.36984 10.448 3.27367C10.4857 3.17749 10.5032 3.07456 10.4992 2.97133C10.4953 2.8681 10.47 2.7668 10.4251 2.67378C10.3802 2.58076 10.3165 2.49802 10.2381 2.43075V2.43075Z" />
    </g>
  </svg>
);

const StartConsultationCard: React.FC = () => {
  const theme: Theme = useTheme();
  const { AuthIsSimpleUser } = useAuth();
  const location = useLocation();

  // Demo reports are sample data and the consultation isn't meaningful for
  // them — hide for everyone (affiliate and customer alike).
  const isDemoReport = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer.isDemo
  );

  if (isDemoReport) return null;

  // The consultation is available to customers and affiliates alike — there is
  // no approval gate on the entry point. Unapproved systems still appear inside
  // the consultation; they just hide their score + bar (see SystemDetailPanel /
  // SystemsSidebar), mirroring the score screen.

  // Affiliates pass the selected client's id via the URL (`?id=...`),
  // matching how every other affiliate-facing screen is parameterised.
  // Customers (simple users) get a clean `/consultation` URL — the
  // `ConsultationProvider` for role=1 sources `customerId` from
  // `AuthUser.pnoe_user_id` directly (it ignores `?id=` for that role
  // specifically). This guarantees a customer can only ever load their
  // own consultation data, even if they hand-edit the URL.
  const params = new URLSearchParams(location.search);
  const urlId = params.get("id");
  const href =
    AuthIsSimpleUser || !urlId
      ? "/consultation"
      : `/consultation?id=${urlId}`;

  const handleClick = () => {
    window.open(href, "_blank", "noopener,noreferrer");
  };

  return (
    <Box sx={styles.card(theme)}>
      <Box sx={styles.text}>
        <Box sx={styles.title(theme)}>Ready to discover your Consultation?</Box>
        <Box sx={styles.body(theme)}>
          Use our AI guide or explore on your own. Get your personalized
          longevity protocol now.
        </Box>
      </Box>
      <Box sx={styles.actions}>
        <PrimaryButton
          label="Get started"
          icon={<PersonalTrainerIcon />}
          onClick={handleClick}
          sx={styles.cta}
        />
      </Box>
    </Box>
  );
};

export default StartConsultationCard;

const styles = {
  card: (theme: Theme) => ({
    width: "100%",
    boxSizing: "border-box",
    padding: "12px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    // Figma gradient: light blue → white. The light blue (#E6F0FF) at 70.5%
    // alpha maps to colorTokens.P[10] with hex alpha B4 (≈70.6%). Final
    // opaque stop is the standard background.
    backgroundImage: `linear-gradient(110.41deg, ${theme.colorTokens.P[10]}B4 42.102%, ${theme.colors_ds.NEUTRAL_BG_MAIN} 95.178%)`,
    boxShadow:
      "0px 2px 8px rgba(0, 0, 0, 0.04), 0px 8px 20px rgba(0, 0, 0, 0.06)",
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
    marginBottom: "16px",
    overflow: "hidden",
  }),
  text: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
  },
  title: (theme: Theme) => ({
    margin: 0,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: 1.4,
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  body: (theme: Theme) => ({
    margin: 0,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: 1.6,
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  actions: {
    display: "flex",
    justifyContent: "flex-start",
  },
  cta: {
    // Match Figma sizing for this specific card (smaller than the default
    // 14px / 38px PrimaryButton).
    height: "auto",
    padding: "8px 12px",
    fontSize: "12px",
    // PrimaryButton wraps the label in a flex:1 Box; for icon+label packed
    // tightly we want no flex grow.
    "& > div": { flex: "0 0 auto" },
  },
};
